import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import UserGroup from '../Components/user-group/user-group';
import ShipmentMode from '../Components/shipment-mode/shipment-mode';
import ListingStatus from '../Components/listing-status/listing-status';
import { isNotificationExistForShipment } from '../helpers/notificationHelper';
import { getUserTimeZoneDateFormat } from '../helpers/dateHelper';
import {
  ChatIcon,
  Co2CloudIcon,
  // EditIcon,
  AcceptIcon,
  ReceiptEditIcon,
  EditsquareIcon,
  PendingTime,
  BlueFlowerVerified,
  HistoryIcon,
  InfoIcon,
} from '../Components/icons/icons';
import CountCard from '../Components/count-card/count-card';
import { DandDModeGroup } from '../Components/d&d-modes/d&d-modes';
import NumberCard from '../Components/number-card/number-card';
import AdvancePopover from '../Components/advance-popover/advance-popover';
import BesicTable from '../Components/table/basicTable';
import { formatNumberUpTo3Decimals, isNumber } from '../helpers/utils';
import { hasNoOrderPerm } from '../helpers/permissionHelper';
import { giveProfile } from '../Components/global-notification/notification-list';
import IconButton from '../Components/icon-button/icon-button';
import Button from '../Components/button/button';
import Action from '../Components/action/action';
// import TextBoxGroup from '../Components/text-box-group/text-box-group';
import TextBoxWithGroup from '../Components/text-box-group/text-box-with-group';
import OutsideClick from '../Components/outside-click/outside-click';
import Tooltip from '../Components/tooltip/tooltip';
import UserCounter from '../Components/user-counter/user-counter';
// import RejectIcon from '../Components/icons/reject';
import CuiIndexBattery from '../Components/CUI-index/cui-indexbattery';
import Skeleton from '../Components/skeleton/skeleton';

const getUnit = (pkgUnit) => {
  switch (pkgUnit) {
    case 'metric':
      return 'cm';
    case 'imperial':
      return 'in';
    default:
      return '';
  }
};

export const lineItemColumnsShipment = (
  cargoReady,
  isPODependent,
  bookingType,
  pkgWtUnit
) =>
  [
    {
      Header: () => {
        const [t] = useTranslation('manageorders');
        return <>{t('manageordersscreen.lineItembtn.itemid')}</>;
      },
      accessor: 'itemCode',
    },
    isPODependent
      ? {
          Header: () => {
            const [t] = useTranslation('manageorders');
            return <>{t('manageordersscreen.lineItembtn.orderid')}</>;
          },
          accessor: 'order',
          Cell: (data) => {
            const { row } = data;
            const { relation, companyId } = useParams();
            return (
              <>
                {!['forwarder', 'customBroker'].includes(relation) &&
                !hasNoOrderPerm(giveProfile(relation), 'Order') &&
                bookingType !== 'consolidated' ? (
                  <Link
                    to={`/app/${relation}/${companyId}/order-details/${
                      row?.original?.orderId || row?.original?.omnipoId || ''
                    }`}
                  >
                    {row.original.poNumber}
                  </Link>
                ) : (
                  row.original.poNumber
                )}
                <span />
              </>
            );
          },
        }
      : null,

    ...(cargoReady
      ? [
          {
            Header: () => {
              const [t] = useTranslation('manageorders');
              return <>{t('manageordersscreen.lineItembtn.cargoready')}</>;
            },
            align: 'center',
            accessor: 'dueDate',
            type: 'date',
            Cell: (data) => {
              const {
                cell: {
                  row: {
                    original: { dueDate },
                  },
                },
              } = data;

              if (dueDate == null) {
                return <span>--</span>;
              }

              return getUserTimeZoneDateFormat(dueDate);
            },
          },
        ]
      : []),

    {
      Header: () => {
        const [t] = useTranslation('manageorders');
        return <>{t('manageordersscreen.lineItembtn.quantitytoship')}</>;
      },
      accessor: 'qtyShipped',
      align: 'center',
    },
    {
      Header: () => {
        const [t] = useTranslation('manageorders');
        return <>{t('manageordersscreen.lineItembtn.dimensions')}</>;
      },
      accessor: 'Dimensions (LXWXH)',
      align: 'center',
      Cell: (data) => {
        const {
          row: {
            original: { dimensions },
          },
        } = data;

        const length = dimensions?.[0]?.length;
        const width = dimensions?.[0]?.width;
        const height = dimensions?.[0]?.height;
        const unit = getUnit(pkgWtUnit);

        if (length == null || width == null || height == null) {
          return <span className="no-wrap text-center d-block">--</span>;
        }

        return (
          <span className="no-wrap text-center d-block">
            {isNumber(length) && isNumber(width) && isNumber(height)
              ? `${length} X ${width} X ${height}${
                  length !== 0 || width !== 0 || height !== 0 ? ` ${unit}` : ''
                }`
              : '--'}
          </span>
        );
      },
    },

    {
      Header: () => {
        const [t] = useTranslation('manageorders');
        return <>{t('manageordersscreen.lineItembtn.weight')}</>;
      },
      accessor: 'Weight',
      Cell: (data) => {
        const {
          row: {
            original: { grossWeight, grossWeightUnit, weightUnit },
          },
        } = data;

        const unit = grossWeightUnit || weightUnit;

        if (grossWeight == null || unit == null) {
          return <span className="no-wrap">--</span>;
        }
        return <span className="no-wrap">{`${grossWeight} ${unit}`}</span>;
      },
    },
  ].filter(Boolean);
export const BookListOrderRfq = [
  {
    Header: () => {
      const [t] = useTranslation('manageorders');
      return <>{t('manageordersscreen.lineItembtn.productname')}</>;
    },
    accessor: 'packageName',
    Cell: (data) => {
      const { row } = data;
      const { packageName } = row.original;
      return packageName || '--'; // No Fragment needed, return single element directly
    },
    align: 'center',
  },
  {
    Header: () => {
      const [t] = useTranslation('manageorders');
      return <>{t('manageordersscreen.lineItembtn.dimensions')}</>;
    },
    accessor: 'Dimensions (LXWXH)',
    Cell: (data) => {
      const { row } = data;
      const { dimensions, measurementUnit } = row.original;
      const { length, width, height } = dimensions?.[0] || {};
      const units = getUnit(measurementUnit);
      return (
        <span className="no-wrap text-center d-block">
          {isNumber(length) && isNumber(width) && isNumber(height)
            ? `${length} X ${width} X ${height}${
                length !== 0 || width !== 0 || height !== 0 ? ` ${units}` : ''
              }`
            : '--'}
        </span>
      );
    },
    align: 'center',
  },
  {
    Header: () => {
      const [t] = useTranslation('manageorders');
      return <>{t('manageordersscreen.lineItembtn.quantity-key')}</>;
    },
    accessor: 'qtyShipped',
    Cell: (data) => {
      const { row } = data;
      const { qtyShipped } = row.original;
      return isNumber(qtyShipped) ? qtyShipped : '--';
    },
    align: 'center',
  },
  {
    Header: () => {
      const [t] = useTranslation('manageorders');
      return <>{t('manageordersscreen.lineItembtn.weight')}</>;
    },
    accessor: 'weight',
    Cell: (data) => {
      const { row } = data;
      const { weight, measurementUnit } = row.original;
      return isNumber(weight)
        ? `${weight} ${measurementUnit === 'metric' ? 'kgs' : 'lbs'}`
        : '--';
    },
    align: 'center',
  },
];

export const BookListOrder2 = [
  {
    Header: () => {
      const [t] = useTranslation('managebooking');
      return <>{t('managebookingscreen.ordersmenu.order')}</>;
    },
    accessor: 'order',
    Cell: (data) => {
      const {
        row,
        row: {
          values: { order },
        },
      } = data;
      const { relation, companyId } = useParams();
      return (
        <>
          {!['forwarder', 'customBroker'].includes(relation) &&
          !hasNoOrderPerm(giveProfile(relation), 'Order') ? (
            <Link
              to={`/app/${relation}/${companyId}/order-details/${
                row?.original?.orderId || ''
              }`}
            >
              {order}
            </Link>
          ) : (
            order
          )}
          <span />
        </>
      );
    },
  },
  {
    Header: () => {
      const [t] = useTranslation('managebooking');
      return <>{t('managebookingscreen.ordersmenu.item')}</>;
    },
    accessor: 'orderItem',
  },
  {
    Header: () => {
      const [t] = useTranslation('managebooking');
      return <>{t('managebookingscreen.ordersmenu.cargoready')}</>;
    },
    align: 'center',
    accessor: 'cargoReady',
  },
  // {
  //   Header: () => {
  //     const [t] = useTranslation('managebooking');
  //     return <>{t('managebookingscreen.ordersmenu.status')}</>;
  //   },
  //   align: 'center',
  //   accessor: 'orderStatus',
  // },
];

/* eslint-disable react/prop-types */
export const customsItemsSubColumns = [
  {
    Header: () => {
      const [t] = useTranslation('manageshipments');
      return (
        <>
          {t(
            'manageshipmentsscreen.shipmentdetails.customClearance.subTable.order'
          )}
        </>
      );
    },
    accessor: 'poNumber',
    align: 'left',
    Cell: ({ row }) => {
      const { poNumber } = row.original;
      return poNumber || '--';
    },
  },
  {
    Header: () => {
      const [t] = useTranslation('manageshipments');
      return (
        <>
          {t(
            'manageshipmentsscreen.shipmentdetails.customClearance.subTable.orderItem'
          )}
        </>
      );
    },
    accessor: 'itemSequenceId',
    align: 'left',
    Cell: ({ row }) => {
      const { itemSequenceId } = row.original;
      return itemSequenceId || '--';
    },
  },
  {
    Header: () => {
      const [t] = useTranslation('manageshipments');
      return (
        <>
          {t(
            'manageshipmentsscreen.shipmentdetails.customClearance.subTable.materialGroup'
          )}
        </>
      );
    },
    accessor: 'materialGroup',
    align: 'left',
    Cell: ({ row }) => {
      const { materialGroup } = row.original;
      return materialGroup || '--';
    },
  },
  {
    Header: () => {
      const [t] = useTranslation('manageshipments');
      return (
        <>
          {t(
            'manageshipmentsscreen.shipmentdetails.customClearance.subTable.materialID'
          )}
        </>
      );
    },
    accessor: 'materialId',
    align: 'left',
    Cell: ({ row }) => {
      const { materialId } = row.original;
      return materialId || '--';
    },
  },
  {
    Header: () => {
      const [t] = useTranslation('manageshipments');
      return (
        <>
          {t(
            'manageshipmentsscreen.shipmentdetails.customClearance.subTable.shippedQty'
          )}
        </>
      );
    },
    accessor: 'quantity',
    align: 'center',
    Cell: ({ row }) => {
      const { quantity } = row.original;
      return (
        <NumberCard
          size="small"
          status="primary"
          value={formatNumberUpTo3Decimals(quantity)}
        />
      );

      // return quantity != null ? (
      //   <span className="qty-pill">{formatNumberUpTo3Decimals(quantity)}</span>
      // ) : (
      //   '--'
      // );
    },
  },
];

export const customsDocsSubColumns = [
  {
    Header: () => {
      const [t] = useTranslation('manageshipments');
      return (
        <>
          {t(
            'manageshipmentsscreen.shipmentdetails.customClearance.subTable.document'
          )}
        </>
      );
    },
    accessor: 'docName',
    align: 'left',
    Cell: ({ row }) => {
      const { docName, link } = row.original;

      return link ? (
        <a
          href={link}
          target="_blank"
          rel="noopener noreferrer"
          className="link-text"
        >
          {docName}
        </a>
      ) : (
        docName || '--'
      );
    },
  },

  {
    Header: () => {
      const [t] = useTranslation('manageshipments');
      return (
        <>
          {t(
            'manageshipmentsscreen.shipmentdetails.customClearance.subTable.verificationStatus'
          )}
        </>
      );
    },
    accessor: 'verified',
    align: 'center',
    Cell: ({ row }) => {
      const isVerified = row.original.status?.toLowerCase() === 'verified';
      const [t] = useTranslation('manageshipments');
      return (
        <Tooltip
          content={
            isVerified
              ? t(
                  'manageshipmentsscreen.shipmentdetails.customsclearancebtn.verified'
                )
              : t(
                  'manageshipmentsscreen.shipmentdetails.customsclearancebtn.pending'
                )
          }
          zIndex={100000010}
          direction="left"
        >
          <span>{isVerified ? <BlueFlowerVerified /> : <PendingTime />}</span>
        </Tooltip>
      );
    },
  },
  // {
  //   Header: 'Actions',
  //   accessor: 'actions',
  //   align: 'center',
  //   Cell: ({ row }) => {
  //     const isVerified = row.original.status?.toLowerCase() === 'approved';

  //     return (
  //       <div className="d-flex justify-center">
  //         <Tooltip content="Verify">
  //           <span className="mr-10">
  //             <IconButton
  //               variant="accept"
  //               disabled={isVerified}
  //               onClick={() => {
  //                 console.log('Verify clicked', row.original);
  //               }}
  //             >
  //               <AcceptIcon size="18" />
  //             </IconButton>
  //           </span>
  //         </Tooltip>

  //         <Tooltip content="Reject / Request">
  //           <IconButton
  //             variant="default"
  //             onClick={() => {
  //               console.log('Reject clicked', row.original);
  //             }}
  //           >
  //             <RejectIcon size="18" />
  //           </IconButton>
  //         </Tooltip>
  //       </div>
  //     );
  //   },
  // },
];

/* eslint-disable react/prop-types */
export const customShipmentColumns = [
  {
    Header: () => {
      const [t] = useTranslation('manageshipments');
      return (
        <>
          {t(
            'manageshipmentsscreen.shipmentdetails.customClearance.customsRef'
          )}
        </>
      );
    },
    accessor: 'customsRefNo',
    align: 'center',
    Cell: ({ row }) => (
      <span className="link-text">{row.original.customsRefNo}</span>
    ),
  },
  {
    Header: () => {
      const [t] = useTranslation('manageshipments');
      return (
        <>
          {t(
            'manageshipmentsscreen.shipmentdetails.customClearance.customsDeclaration'
          )}
        </>
      );
    },
    accessor: 'customDeclaration',
    align: 'center',
  },
  {
    Header: () => {
      const [t] = useTranslation('manageshipments');
      return (
        <>
          {t('manageshipmentsscreen.shipmentdetails.customClearance.ralNumber')}
        </>
      );
    },
    accessor: 'ralNumbers',
    align: 'center',
    Cell: (data) => {
      const {
        row: { original },
      } = data;

      const ralNumbers = original?.ralNumbers ?? [];

      return ralNumbers.length > 0 ? (
        <span className="d-inline-flex align-center button-border-hover">
          <span className="link">{ralNumbers[0]}</span>

          {ralNumbers.length > 1 && (
            <Action
              label={`+${ralNumbers.length - 1}`}
              className="icon-button ml-5 primary s-small justify-center"
              zIndex={1000001}
            >
              {ralNumbers.slice(1).map((item) => (
                <button key={item} type="button">
                  {item}
                </button>
              ))}
            </Action>
          )}
        </span>
      ) : (
        '--'
      );
    },
  },

  {
    Header: () => {
      const [t] = useTranslation('manageshipments');
      return (
        <>
          {t(
            'manageshipmentsscreen.shipmentdetails.customClearance.customsStartedDate'
          )}
        </>
      );
    },
    accessor: 'customStartedDate',
    align: 'center',
  },
  {
    Header: () => {
      const [t] = useTranslation('manageshipments');
      return (
        <>
          {t(
            'manageshipmentsscreen.shipmentdetails.customClearance.customsCompDate'
          )}
        </>
      );
    },
    accessor: 'customCompletedDate',
    align: 'center',
  },
  {
    Header: () => {
      const [t] = useTranslation('manageshipments');
      return (
        <>{t('manageshipmentsscreen.shipmentdetails.customClearance.item')}</>
      );
    },
    accessor: 'itemsCount',
    align: 'center',
    Cell: ({ row }) => {
      const items = row.original?.orderItems ?? [];

      return (
        <CountCard
          label={items.length}
          data={items}
          columns={customsItemsSubColumns}
          enableDynamicTable={true}
          align="center"
          popoverPlacement="left"
          zIndex="1000010"
          id={`itemsCol-${row.original._id || row.index}`}
        />
      );
    },
  },
  {
    Header: () => {
      const [t] = useTranslation('manageshipments');
      return (
        <>{t('manageshipmentsscreen.shipmentdetails.customClearance.status')}</>
      );
    },
    accessor: 'status',
    align: 'center',
    Cell: ({ row }) => {
      return (
        <ListingStatus
          statusLabel={row.original.status}
          textClass={
            row.original.status?.toLowerCase().startsWith('customs')
              ? 'text-wrap'
              : ''
          }
        />
      );
    },
  },
  {
    Header: () => {
      const [t] = useTranslation('manageshipments');
      return (
        <>
          {t('manageshipmentsscreen.shipmentdetails.customClearance.docStatus')}
        </>
      );
    },
    accessor: 'documentStatusCount',
    align: 'center',
    Cell: ({ row }) => {
      const docs = row.original?.documentStatus ?? [];

      return (
        <CountCard
          label={docs.length}
          data={docs}
          columns={customsDocsSubColumns}
          enableDynamicTable={true}
          align="center"
          popoverPlacement="left"
          zIndex="1000010"
          id={`doc-status-${row.original._id || row.index}`}
        />
      );
    },
  },
];

export const shipmentColumns = (fetchCustomDetails) => [
  {
    Header: () => {
      const [t] = useTranslation('manageshipments');
      return <>{t('manageshipmentsscreen.shipmentscardrow.participants')}</>;
    },
    accessor: 'participants',
    Cell: (data) => {
      const {
        row: { original },
      } = data;
      return (
        <div className="table-col-row">
          <UserGroup
            userData={original?.uniqueUserIds || []}
            moreItemsDetails={{
              id: original?.shipmentId,
              module: 'shipment',
              count: original?.uniqueUserCount,
              calledAt: 'PO',
            }}
          />
        </div>
      );
    },
  },
  {
    Header: () => {
      const [t] = useTranslation('manageshipments');
      return <>{t('manageshipmentsscreen.shipmentscardrow.mode')}</>;
    },
    accessor: 'mode',
    Cell: (data) => {
      const { row } = data;
      return (
        <ShipmentMode
          shipmentMode={row?.original?.mode}
          bookingType={row?.original?.bookingType}
        />
      );
    },
  },

  // {
  //   Header: 'Consignee',
  //   accessor: 'ConsigneeName',
  // },
  // {
  //   Header: 'Shipper',
  //   accessor: 'ShipperName',
  // },
  {
    Header: () => {
      const [t] = useTranslation('manageshipments');
      return <>{t('manageshipmentsscreen.shipmentscardrow.origin')}</>;
    },
    accessor: 'originFacility',
  },
  {
    Header: () => {
      const [t] = useTranslation('manageshipments');
      return <>{t('manageshipmentsscreen.shipmentscardrow.destination')}</>;
    },
    accessor: 'destinationFacility',
  },
  {
    Header: () => {
      const [t] = useTranslation('manageshipments');
      return <>{t('manageshipmentsscreen.shipmentscardrow.customs')}</>;
    },
    accessor: 'customs',
    align: 'center',

    Cell: ({ row }) => {
      const { shipmentId, currentCustomStatus, CustomCount } = row.original;
      const { relation } = useParams();
      const [t] = useTranslation('status');
      const [customsByShipmentId, setCustomsByShipmentId] = useState({});
      const [isCustomStatusLoading, setIsCustomStatusLoading] = useState(false);
      const label =
        currentCustomStatus && currentCustomStatus !== '--'
          ? currentCustomStatus
          : customsByShipmentId?.[0]?.customStatus || 'Not Started';
      const handleClick = async () => {
        if (!shipmentId || customsByShipmentId[shipmentId]) return;

        try {
          setIsCustomStatusLoading(true);

          const { data } = await fetchCustomDetails({
            variables: {
              input: {
                profileType: giveProfile(relation),
                shipmentId,
              },
            },
          });
          const detail = data?.FetchCustomDetail?.data || [];
          if (!detail) return;
          setCustomsByShipmentId((prev) => ({
            ...prev,
            [shipmentId]: detail,
          }));
        } catch (err) {
          console.error(err);
        } finally {
          setIsCustomStatusLoading(false);
        }
      };
      return (
        <CountCard
          data={customsByShipmentId?.[shipmentId] ?? []}
          columns={customShipmentColumns}
          enableDynamicTable={true}
          disableClick={CustomCount === 0}
          onClick={handleClick}
          align="center"
          popoverPlacement="right-center"
          tableClass="w-760-px"
          disableBorder
          labelClass="d-flex align-center"
          isLoading={isCustomStatusLoading}
        >
          <span className="max-w-80 d-block text-wrap">
            {t(`statuslist.status.${label}`)}
          </span>
          {CustomCount !== 0 && (
            <span className="ml-5">
              <UserCounter text={CustomCount} size="xxx-small" />
            </span>
          )}
        </CountCard>
      );
    },
  },
  {
    Header: () => {
      const [t] = useTranslation('manageshipments');
      return <>{t('manageshipmentsscreen.shipmentscardrow.status')}</>;
    },
    accessor: 'currentStatus',
    align: 'center',
    Cell: (data) => {
      const {
        row: {
          values: { currentStatus },
        },
      } = data;
      return <ListingStatus statusLabel={currentStatus} />;
    },
  },
  {
    Header: () => {
      const [t] = useTranslation('manageshipments');
      return <>{t('manageshipmentsscreen.shipmentscardrow.ETA')}</>;
    },
    accessor: 'ETA',
    Cell: (data) => {
      const { row } = data;

      return row.original.ETA && row.original.ETA !== '--'
        ? getUserTimeZoneDateFormat(row.original.ETA)
        : '--';
    },
    align: 'center',
  },
  {
    Header: () => {
      const [t] = useTranslation('manageshipments');
      return <>{t('manageshipmentsscreen.shipmentscardrow.event')}</>;
    },
    accessor: 'event',
    align: 'center',
    Cell: (data) => {
      const {
        row: {
          values: { event },
        },
      } = data;
      return <ListingStatus statusLabel={event} />;
    },
  },
  ...(false
    ? [
        {
          Header: () => {
            const [t] = useTranslation('manageshipments');
            return (
              <>{t('manageshipmentsscreen.shipmentscardrow.notification')}</>
            );
          },
          accessor: 'notification',
          align: 'center',
          Cell: (data) => {
            const { row } = data;
            const { relation, companyId } = useParams();
            return (
              <Link
                to={`/app/${relation}/${companyId}/shipment-details/${row?.original?.shipmentId}`}
                className="list-chat-icon"
              >
                {isNotificationExistForShipment(row?.original?.shipmentId) ===
                  true && <span className="count">&nbsp;</span>}
                <span className="number">
                  <ChatIcon color="#1D3E69" />
                </span>
              </Link>
            );
          },
        },
      ]
    : []),
];
export const ShipmentWithOrder = [
  {
    Header: () => {
      const [t] = useTranslation('manageshipments');
      return <>{t('manageshipmentsscreen.shipmentscardrow.shipment')}</>;
    },
    accessor: 'shipmentNo',
    Cell: (data) => {
      const {
        row,
        row: {
          values: { shipmentNo },
        },
      } = data;
      const { relation, companyId } = useParams();
      return (
        <Link
          to={`/app/${relation}/${companyId}/shipment-details/${row?.original?.shipmentId}`}
          className="link"
        >
          {shipmentNo.slice(0, 20)}
          {shipmentNo.length > 20 ? '...' : ''}
        </Link>
      );
    },
  },
  {
    Header: () => {
      const [t] = useTranslation('manageshipments');
      return <>{t('manageshipmentsscreen.shipmentscardrow.blno')}</>;
    },
    accessor: 'BLno',
    Cell: (data) => {
      const {
        row: {
          values: { BLno },
          original: { HBLNo },
        },
      } = data;
      return (
        <p>
          {BLno.slice(0, 20) || HBLNo.slice(0, 20)}
          {BLno.length > 20 ? '...' : ''}
          {HBLNo.length > 20 ? '...' : ''}
        </p>
      );
    },
  },
  {
    Header: () => {
      const [t] = useTranslation('manageshipments');
      return <>{t('manageshipmentsscreen.shipmentscardrow.order')}</>;
    },
    accessor: 'orderNo',
    Cell: (data) => {
      const {
        row: {
          original: { packages, poNumber, orderID, orderNo, isRFQ },
        },
      } = data;
      let totalOrders = [];
      const lineItemsData = [];
      packages?.forEach((pkg) =>
        pkg?.lineItems?.forEach((item) => {
          lineItemsData?.push({
            orderId: item?.poID || item?.orderId || orderID || item?.omnipoId,
            order: item?.poNumber || poNumber || orderNo,
            orderItem: item?.itemCode,
            qtyShipped: item?.qtyShipped,
            orderStatus: item?.statusName,
            cargoReady: getUserTimeZoneDateFormat(item?.dueDate),
            dimensions: item?.dimensions,
            packageName: item?.itemName,
            weight: item?.grossWeight,
            measurementUnit: item?.measurementUnit,
          });
          totalOrders.push(item?.poNumber || poNumber);
        })
      );
      totalOrders = [...new Set(totalOrders)];
      const [t] = useTranslation('manageshipments');
      return (
        <>
          <CountCard
            data={lineItemsData}
            label={
              totalOrders?.length === 1
                ? `${totalOrders?.length} ${t(
                    'manageshipmentsscreen.orderSingular'
                  )}`
                : `${totalOrders?.length || 0} ${t(
                    'manageshipmentsscreen.orderPlural'
                  )}`
            }
            enableDynamicTable={true}
            columns={isRFQ ? BookListOrderRfq : BookListOrder2}
            disableBorder
          />
          <span />
        </>
      );
    },
  },
  {
    Header: 'D&D',
    accessor: 'danddtype',
    width: '100',
    Cell: (data) => {
      const { row } = data;
      const dndCounts = row?.original?.dndCounts;

      return dndCounts?.length > 0 ? <DandDModeGroup data={dndCounts} /> : '--';
    },
  },
  {
    Header: () => {
      return (
        <>
          CO<span className="power">2</span>e
        </>
      );
    },
    accessor: 'co2Data',
    Cell: () => {
      return (
        <div className="d-flex flex-column align-center">
          <Co2CloudIcon />
          <p className="no-wrap m-0">491.27 Kg</p>
        </div>
      );
    },
  },
];
export const ShipmentWithoutOrder = [
  {
    Header: () => {
      const [t] = useTranslation('manageshipments');
      return <>{t('manageshipmentsscreen.shipmentscardrow.blno')}</>;
    },
    accessor: 'BLno',
    Cell: (data) => {
      const {
        row,
        row: {
          values: { BLno },
          original: { HBLNo },
        },
      } = data;
      const { relation, companyId } = useParams();
      return (
        <Link
          to={`/app/${relation}/${companyId}/shipment-details/${row?.original?.shipmentId}`}
        >
          {BLno?.slice(0, 20) || HBLNo?.slice(0, 20)}
          {BLno?.length > 20 ? '...' : ''}
          {HBLNo?.length > 20 ? '...' : ''}
        </Link>
      );
    },
  },
  {
    Header: () => {
      const [t] = useTranslation('manageshipments');
      return (
        <p className="no-wrap">
          {t('manageshipmentsscreen.shipmentscardrow.shipment')}
        </p>
      );
    },
    accessor: 'shipmentNo',
    Cell: (data) => {
      const {
        row: {
          values: { shipmentNo },
        },
      } = data;
      // const { relation, companyId } = useParams();
      return (
        <p>
          {shipmentNo.slice(0, 20)}
          {shipmentNo.length > 20 ? '...' : ''}
        </p>
      );
    },
  },
  {
    Header: 'D&D',
    accessor: 'danddtype',
    width: '100',
    Cell: (data) => {
      const { row } = data;
      const dndCounts = row?.original?.dndCounts;
      return dndCounts?.length > 0 ? <DandDModeGroup data={dndCounts} /> : '--';
    },
  },
  {
    Header: () => {
      return (
        <>
          CO<span className="power">2</span>e
        </>
      );
    },
    accessor: 'co2Data',
    Cell: (data) => {
      const { row } = data;
      const co2 = row?.original?.co2;
      return (
        <div className="d-flex flex-column align-center">
          {co2 !== '--' && <Co2CloudIcon />}
          <p className="no-wrap m-0">{co2}</p>
        </div>
      );
    },
  },
];

const buyerBookingDetailsColumns = (
  cargoReady,
  isPODependent,
  isRFQ,
  bookingType
) => {
  const columnsData = [
    {
      Header: () => {
        const [t] = useTranslation('manageorders');
        return <>{t('manageordersscreen.bookingdetailsbtn.packagetype')}</>;
      },
      accessor: 'packageType',
    },
    {
      Header: () => {
        const [t] = useTranslation('manageorders');
        return <>{t('manageordersscreen.bookingdetailsbtn.quantity')}</>;
      },
      accessor: 'pkgQty',
      align: 'center',
      Cell: (data) => {
        const { row } = data;
        const [t] = useTranslation('manageorders');
        const quantity = row?.original?.pkgQty ?? '--';
        return (
          <div className="d-flex justify-center">
            {row.original.isVendorPackage ? (
              'NA'
            ) : (
              <NumberCard
                value={quantity}
                status="primary"
                // {bookingDetailsPage ? 'primary' : 'purple'}
                content={t('manageordersscreen.bookingdetailsbtn.tooltip')}
              />
            )}
          </div>
        );
      },
    },
    {
      Header: () => {
        const [t] = useTranslation('manageorders');
        return <>{t('manageordersscreen.bookingdetailsbtn.dimensions')}</>;
      },
      accessor: 'Dimensions (LXWXH)',
      align: 'center',
      Cell: (data) => {
        const {
          row: { original },
        } = data;
        const length = isNumber(original?.dimentions?.length)
          ? original?.dimentions?.length
          : original?.dimensions?.length;
        const width = isNumber(original?.dimentions?.width)
          ? original?.dimentions?.width
          : original?.dimensions?.width;
        const height = isNumber(original?.dimentions?.height)
          ? original?.dimentions?.height
          : original?.dimensions?.height;
        const units = getUnit(original?.pkgWtUnit);
        return (
          <span className="no-wrap text-center d-block">
            {(() => {
              if (original?.isVendorPackage) return 'NA';

              if (isNumber(length) && isNumber(width) && isNumber(height)) {
                return `${length} X ${width} X ${height} ${units}`;
              }

              return '--';
            })()}
          </span>
        );
      },
    },

    {
      Header: () => {
        const [t] = useTranslation('manageorders');
        return <>{t('manageordersscreen.bookingdetailsbtn.grossweight')}</>;
      },
      accessor: 'Gross Weight',
      align: 'center',
      Cell: (data) => {
        const {
          row: {
            original: { pkgGrossWt, pkgWtUnit, isVendorPackage },
          },
        } = data;
        let displayValue = '';

        if (isVendorPackage) {
          displayValue = 'NA';
        } else if (pkgGrossWt == null || pkgWtUnit == null) {
          displayValue = '--';
        } else {
          displayValue = `${pkgGrossWt} ${
            pkgWtUnit === 'imperial' ? 'Lbs' : 'Kgs'
          }`;
        }

        return <span className="no-wrap">{displayValue}</span>;
      },
    },
    {
      Header: () => {
        const [t] = useTranslation('manageorders');
        return <>{t('manageordersscreen.bookingdetailsbtn.lineitems')}</>;
      },
      accessor: 'Line Items',
      align: 'center',
      Cell: (data) => {
        const [eventValue, setEventValue] = useState(null);
        const togglePopover = (e) => {
          setEventValue(e.target);
        };
        const handleClose = () => {
          setEventValue(null);
        };
        const {
          cell: {
            row: {
              original: { lineItems, poNumber, pkgWtUnit },
            },
          },
        } = data;
        const [t] = useTranslation('manageorders');

        const displayLineItems = lineItems || [];
        const displayPoNumber = poNumber || '--';

        return (
          <>
            <button
              className="item-selected-button"
              type="button"
              onClick={togglePopover}
              id="countPopover"
            >
              {displayLineItems.length}{' '}
              {displayLineItems.length > 1
                ? t('manageordersscreen.lineItembtn.productsbtn')
                : t('manageordersscreen.lineItembtn.productbtn')}
            </button>
            <AdvancePopover
              reference="countPopover"
              getEvent={eventValue}
              closePopover={handleClose}
              placement="left"
            >
              <div className="count-card-table">
                <BesicTable
                  columns={
                    isRFQ
                      ? BookListOrderRfq
                      : lineItemColumnsShipment(
                          cargoReady,
                          isPODependent,
                          bookingType,
                          pkgWtUnit
                        )
                  }
                  data={displayLineItems.map((item) => ({
                    ...item,
                    poNumber: item?.poNumber || displayPoNumber,
                    dimensions: item?.dimensions,
                    packageName: item?.itemName,
                    weight: item?.grossWeight,
                  }))}
                  className="auto-column-width"
                />
              </div>
            </AdvancePopover>
          </>
        );
      },
    },
  ];
  return columnsData;
};

export { buyerBookingDetailsColumns };
export const containerDetailsColumn = [
  {
    Header: () => {
      const [t] = useTranslation('manageshipments');
      return (
        <p className="no-wrap">
          {t('manageshipmentsscreen.shipmentscardrow.Carrier & Container')}
        </p>
      );
    },
    accessor: 'ContainerNo',
    align: 'left',
    Cell: (data) => {
      const {
        row: {
          original: { ContainerNo },
        },
      } = data;

      return <span>{ContainerNo || '--'}</span>;
    },
  },
  {
    Header: () => {
      const [t] = useTranslation('manageshipments');
      return (
        <p className="no-wrap">
          {t('manageshipmentsscreen.shipmentscardrow.Tracking Link')}
        </p>
      );
    },
    accessor: 'trackingLink',
    align: 'center',
    Cell: (data) => {
      const {
        row: {
          original: { trackingLink },
        },
      } = data;

      return <span>{trackingLink || '--'}</span>;
    },
  },
  {
    Header: () => {
      const [t] = useTranslation('manageshipments');
      return (
        <p className="no-wrap">
          {t('manageshipmentsscreen.shipmentscardrow.Container Type')}
        </p>
      );
    },
    accessor: 'containerType',
    align: 'center',
    Cell: (data) => {
      const {
        row: {
          original: { containerType },
        },
      } = data;

      return <span>{containerType || '--'}</span>;
    },
  },
  {
    Header: () => {
      const [t] = useTranslation('manageshipments');
      return (
        <p className="no-wrap">
          {t('manageshipmentsscreen.shipmentscardrow.status')}
        </p>
      );
    },
    accessor: 'status',
    align: 'center',
    Cell: (data) => {
      const {
        row: {
          original: { status },
        },
      } = data;

      return <span>{status || '--'}</span>;
    },
  },
  {
    Header: () => {
      const [t] = useTranslation('manageshipments');
      return (
        <p className="no-wrap">
          {t('manageshipmentsscreen.shipmentscardrow.CUIIndex')}
        </p>
      );
    },
    accessor: 'cui',
    align: 'center',
    Cell: (data) => {
      const {
        row: {
          original: { cui },
        },
      } = data;
      const [t] = useTranslation('manageshipments');

      return (
        <Tooltip
          placement="center"
          content={`${cui}${t(
            'manageshipmentsscreen.shipmentscardrow.utilization-percentage'
          )}`}
        >
          {cui ? <CuiIndexBattery value={cui} /> : <span>--</span>}
        </Tooltip>
      );
    },
  },
];

export const shipmentContainerColumns = [
  {
    Header: 'Container #',
    accessor: 'description',
  },
];
export const associatedShipmentsColumn = [
  {
    Header: 'Shipment#',
    accessor: 'shipmentNo',
    Cell: (data) => {
      const {
        row,
        row: {
          values: { shipmentNo },
        },
      } = data;
      const { relation, companyId } = useParams();
      return (
        <Link
          to={`/app/${relation}/${companyId}/shipment-details/${row.original._id}`}
        >
          {shipmentNo}
        </Link>
      );
    },
  },
  {
    Header: 'Mode',
    accessor: 'mode',
    Cell: (data) => {
      const {
        cell: { row },
      } = data;
      return (
        <ShipmentMode
          shipmentMode={row.original.mode}
          bookingType={row.original.bookingType}
        />
      );
    },
  },
  // {
  //   Header: 'Delivery Date',
  //   accessor: 'ETA',
  // },
  {
    Header: 'Origin',
    accessor: 'originFacility',
  },
  {
    Header: 'Destination',
    accessor: 'destinationFacility',
  },
  {
    Header: 'Status',
    accessor: 'currentStatus',
    align: 'center',
    Cell: (data) => {
      const {
        cell: {
          row: {
            original: { currentStatus },
          },
        },
      } = data;
      return <ListingStatus statusLabel={currentStatus} />;
    },
  },
];
export const shipmentRoutingColumns = [
  {
    Header: 'POL',
    accessor: 'pol',
  },
  {
    Header: 'POD',
    accessor: 'pod',
  },
  {
    Header: 'IMO#',
    accessor: 'imoNumber',
  },
  {
    Header: 'Vessel Name',
    accessor: 'vesselname',
  },
  {
    Header: 'Voyage',
    accessor: 'voyage',
  },
  {
    Header: 'Carrier',
    accessor: 'carrier',
  },
  {
    Header: 'ETA',
    accessor: 'eta',
  },
  {
    Header: 'ETD',
    accessor: 'etd',
  },
  {
    Header: 'ATA',
    accessor: 'ata',
  },
  {
    Header: 'ATD',
    accessor: 'atd',
  },
  {
    Header: 'CarrierCode',
    accessor: 'carrierCode',
  },
  {
    Header: 'Flight No',
    accessor: 'flightNo',
  },
  {
    Header: 'Flight No',
    accessor: 'flight_number',
  },
  {
    Header: 'Carrier Name',
    accessor: 'carrierName',
  },
  {
    Header: 'City',
    accessor: 'city',
  },
  {
    Header: 'Code',
    accessor: 'code',
  },
  {
    Header: 'Country',
    accessor: 'country',
  },
  {
    Header: 'Departure Date',
    accessor: 'departure_date',
    type: 'date',
  },
  {
    Header: 'Arrival Date',
    accessor: 'arrival_date',
    type: 'date',
  },
  {
    Header: 'IMO Number',
    accessor: 'imo_number',
  },
  // {
  //   Header: 'Latitude',
  //   accessor: 'lat',
  // },
  // {
  //   Header: 'Longitude',
  //   accessor: 'lng',
  // },
  {
    Header: 'MMSI Number',
    accessor: 'mmsi_number',
  },
  {
    Header: 'Port Name',
    accessor: 'name',
  },
  {
    Header: 'Type',
    accessor: 'type',
  },
  {
    Header: 'Vessel Name',
    accessor: 'vessel_name',
  },
  {
    Header: 'State Province Code',
    accessor: 'stateProvinceCode',
  },
];
export const shipmentDetailsCustomClearanceColumnSubTable = [
  {
    Header: () => {
      const [t] = useTranslation('manageshipments');
      return `${t(
        'manageshipmentsscreen.shipmentdetails.customClearance.subTable.orderItem'
      )} #`;
    },
    accessor: 'itemCode',
    align: 'center',
  },
  {
    Header: () => {
      const [t] = useTranslation('manageshipments');
      return t(
        'manageshipmentsscreen.shipmentdetails.customClearance.subTable.materialDisc'
      );
    },
    accessor: 'itemName',
    align: 'center',
    Cell: (data) => {
      const {
        row: { original },
      } = data;
      return original?.itemName ? original?.itemName : <span>--</span>;
    },
  },
];

export const shipmentDetailsCustomClearanceLoaderHeaders = (relation) => {
  const columns = [
    {
      Header: '',
      accessor: 'customsRefNo',
      align: 'center',
      Cell: () => {
        return <Skeleton skHeight="0.8vw" skWidth="6vw" />;
      },
    },
    {
      Header: '',
      accessor: 'customDeclaration',
      align: 'center',
      Cell: () => {
        return <Skeleton skHeight="0.8vw" skWidth="8vw" />;
      },
    },
    {
      Header: '',
      accessor: 'customStartedDate',
      align: 'center',
      Cell: () => {
        return <Skeleton skHeight="0.8vw" skWidth="7vw" />;
      },
    },
    {
      Header: '',
      accessor: 'customCompletedDate',
      align: 'center',
      Cell: () => {
        return <Skeleton skHeight="0.8vw" skWidth="7vw" />;
      },
    },
    {
      Header: '',
      accessor: 'status',
      align: 'center',
      Cell: () => {
        return (
          <div className="d-flex justify-center">
            <Skeleton skHeight="1vw" skWidth="4vw" skRadius="1vw" />
          </div>
        );
      },
    },
  ];

  // Actions column
  if (relation === 'customBroker' || giveProfile(relation) === 'buyer') {
    columns.push({
      Header: '',
      accessor: 'actions',
      align: 'center',
      setWidth: '10vw',
      Cell: () => {
        return (
          <div className="d-flex justify-center">
            <Skeleton skHeight="1.5vw" skWidth="1.5vw" />
            <Skeleton skHeight="1.5vw" skWidth="1.5vw" skMargin="0 0 0 1vw" />
          </div>
        );
      },
    });
  }

  return columns;
};
export const shipmentDetailsCustomClearanceColumn = (
  fetchCheckListCustoms,
  setcustomCheckListRef,
  setunsavedChanges,
  setcustomfolderparentId,
  getListDocdata,
  relation,
  handleEventClick,
  handleManifestationClick,
  setIsManefestation
) => {
  const columns = [
    {
      Header: () => {
        const [t] = useTranslation('manageshipments');
        return `${t(
          'manageshipmentsscreen.shipmentdetails.customClearance.customsRef'
        )} #`;
      },
      accessor: 'customsRefNo',
      align: 'center',
      Cell: (data) => {
        const {
          row: { original },
        } = data;
        return (
          <CountCard
            label={original.customsRefNo}
            data={original?.cbSubList}
            disableBorder
            iconVisible
            enableDynamicTable
            columns={shipmentDetailsCustomClearanceColumnSubTable}
          />
        );
      },
    },
    {
      Header: () => {
        const [t] = useTranslation('manageshipments');
        return `${t(
          'manageshipmentsscreen.shipmentdetails.customClearance.customsDeclaration'
        )} #`;
      },
      accessor: 'customDeclaration',
      align: 'center',
      Cell: (data) => {
        const {
          row: { original },
        } = data;
        const isEditable = !original?.manifestationComplete;
        const [toggleEdit, setToggleEdit] = useState(false);
        const initialValue = original?.customDeclaration;
        const [t] = useTranslation('manageshipments');
        const [textboxValue, setTextboxValue] = useState(
          original?.customDeclaration
        );
        const textbox = useRef();
        const toggleRef = useRef(null);
        const [toggleOutsideClick, setToggleOutsideClick] = useState(false);
        const [declarationError, setDeclarationError] = useState('');
        const handleValue = (e) => {
          setTextboxValue(e.target.value);
        };
        useEffect(() => {
          if (toggleRef !== null) {
            setToggleEdit(false);
            handleEventClick(
              initialValue,
              textboxValue,
              original?._id,
              original?.shipmentId
            );
          }
        }, [toggleOutsideClick]);
        // useEffect(() => {
        //   if (toggleEdit && textbox.current) {
        //     setTextboxValue(textbox.current.value || '');
        //   }
        // }, [textboxValue]);

        if (toggleEdit && isEditable) {
          return (
            <div className="d-flex justify-center align-center">
              <OutsideClick
                onClickOutside={() => {
                  setToggleOutsideClick((prev) => !prev);
                }}
              >
                <TextBoxWithGroup
                  placeholder={`${t(
                    'manageshipmentsscreen.shipmentdetails.customClearance.customsDeclaration'
                  )} #`}
                  value={textboxValue || ''}
                  onChange={(e) => {
                    let value = e.target.value.replace(/\D/g, '').slice(0, 14);

                    if (value.length > 3 && value.length <= 7) {
                      value = `${value.slice(0, 3)}-${value.slice(3)}`;
                    } else if (value.length > 7) {
                      value = `${value.slice(0, 3)}-${value.slice(
                        3,
                        7
                      )}-${value.slice(7, 14)}`;
                    }

                    handleValue({ ...e, target: { ...e.target, value } });

                    if (value && value.length !== 16) {
                      setDeclarationError(
                        t(
                          'manageshipmentsscreen.shipmentdetails.customsclearancebtn.shipmentinfo.declarationerror'
                        )
                      );
                    } else {
                      setDeclarationError('');
                    }
                  }}
                  maxLength={16}
                  className="w-113-px mb-0"
                  classNamesgroupwrapper="text-box-group-wrapper-border"
                  ref={textbox}
                  // errorMessage={declarationError}
                  onKeyDown={(e) => {
                    const allowedKeys = [
                      'Backspace',
                      'Delete',
                      'ArrowLeft',
                      'ArrowRight',
                      'Tab',
                    ];

                    if (e.ctrlKey || e.metaKey) return;

                    if (!/[0-9]/.test(e.key) && !allowedKeys.includes(e.key)) {
                      e.preventDefault();
                    }
                  }}
                >
                  {declarationError !== '' ? (
                    <Tooltip content={declarationError} placement="right">
                      <InfoIcon color="#ff2323" width="12" height="12" />
                    </Tooltip>
                  ) : (
                    <Button
                      styles="icon"
                      className="button-transparent px-10"
                      icon={<AcceptIcon size="0.833vw" color="#55B938" />}
                      onClick={() => {
                        setToggleEdit(false);
                        handleEventClick(
                          initialValue,
                          textboxValue,
                          original?._id,
                          original?.shipmentId
                        );
                      }}
                    />
                  )}
                </TextBoxWithGroup>
              </OutsideClick>
              {/* <TextBoxGroup
                placeholder="Enter declaration number"
                type="text"
                defaultValue={original?.customDeclaration}
                onChange={(e) => {
                  temp = e.target.value;
                }}
              /> */}
            </div>
          );
        }

        if (original.customDeclaration) {
          return (
            <span className="d-flex justify-center align-center">
              <span className="d-block max-w-200 text-break-word">
                {original.customDeclaration}
              </span>
              {/* <Button
                styles="icon"
                className="button-transparent px-10"
                icon={<EditsquareIcon />}
                onClick={() => setToggleEdit(true)}
              /> */}
              {isEditable && (
                <IconButton
                  onClick={() => setToggleEdit(true)}
                  className="px-10 ml-5"
                  type="noborder-primary"
                  size="small"
                >
                  <EditsquareIcon />
                </IconButton>
              )}
            </span>
          );
        }

        return <span>--</span>;
      },
    },
    {
      Header: () => {
        const [t] = useTranslation('manageshipments');
        return t(
          'manageshipmentsscreen.shipmentdetails.customClearance.customsStartedDate'
        );
      },
      accessor: 'customStartedDate',
      align: 'center',
      Cell: (data) => {
        const {
          row: { original },
        } = data;
        return original.customStartedDate ? (
          original.customStartedDate
        ) : (
          <span>--</span>
        );
      },
    },
    {
      Header: () => {
        const [t] = useTranslation('manageshipments');
        return t(
          'manageshipmentsscreen.shipmentdetails.customClearance.customsCompDate'
        );
      },
      accessor: 'customCompletedDate',
      align: 'center',
      Cell: (data) => {
        const {
          row: { original },
        } = data;
        return original.customCompletedDate ? (
          original.customCompletedDate
        ) : (
          <span>--</span>
        );
      },
    },
    {
      Header: () => {
        const [t] = useTranslation('manageshipments');
        return t(
          'manageshipmentsscreen.shipmentdetails.customClearance.status'
        );
      },
      accessor: 'status',
      align: 'center',
      Cell: (data) => {
        const {
          row: { original },
        } = data;
        return <ListingStatus statusLabel={original?.status} />;
      },
    },
  ];
  if (relation === 'customBroker' || giveProfile(relation) === 'buyer') {
    columns.push({
      Header: () => {
        const [t] = useTranslation('manageshipments');
        return t(
          'manageshipmentsscreen.shipmentdetails.customClearance.actions'
        );
      },
      accessor: 'actions',
      align: 'center',
      Cell: (data) => {
        const {
          row: { original },
        } = data;
        const [t] = useTranslation('manageshipments');
        return (
          <div className="d-flex">
            {relation === 'customBroker' && (
              <IconButton
                verient="bordared-primary"
                size="s-s-x-medium"
                onClick={() => {
                  setunsavedChanges(false);
                  setIsManefestation(original?.manifestationComplete);
                  setcustomCheckListRef(original?.customsRefNo);
                  setcustomfolderparentId(original?.customParentId);
                  getListDocdata({
                    variables: {
                      input: {
                        channelId: original?._id,
                        channelType: 'customClearance',
                        customProcess: true,
                      },
                    },
                  });
                  fetchCheckListCustoms();
                }}
              >
                <ReceiptEditIcon />
              </IconButton>
            )}
            <Tooltip
              content={t(
                'manageshipmentsscreen.shipmentdetails.ManifestationTable.ManifestationHistory'
              )}
            >
              <IconButton
                type={
                  original?.manifestationComplete ? 'green-bg' : 'orange-bg'
                }
                className="ml-12"
                onClick={() => {
                  handleManifestationClick(original);
                  setIsManefestation(original?.manifestationComplete);
                  getListDocdata({
                    variables: {
                      input: {
                        channelId: original?._id,
                        channelType: 'customClearance',
                      },
                    },
                  });
                }}
              >
                <HistoryIcon />
              </IconButton>
            </Tooltip>
          </div>
        );
      },
    });
  }

  return columns;
};

export const shipmentOrderGrnColumns = [
  {
    Header: () => {
      const [t] = useTranslation('manageshipments');
      return `${t(
        'manageshipmentsscreen.shipmentdetails.orderItems.GRNcolumns.grnqty'
      )}`;
    },
    accessor: 'GRNQty',
    align: 'center',
    Cell: (data) => {
      const {
        row: { original },
      } = data;
      return original.grnQty ? (
        formatNumberUpTo3Decimals(original.grnQty)
      ) : (
        <span>--</span>
      );
    },
  },
  {
    Header: () => {
      const [t] = useTranslation('manageshipments');
      return t(
        'manageshipmentsscreen.shipmentdetails.orderItems.GRNcolumns.debitCreditInd'
      );
    },
    accessor: 'grnDebitCreditInd',
    align: 'center',
    Cell: (data) => {
      const {
        row: { original },
      } = data;
      return original?.grnDebitCreditInd ? (
        original?.grnDebitCreditInd
      ) : (
        <span>--</span>
      );
    },
  },
  {
    Header: () => {
      const [t] = useTranslation('manageshipments');
      return `${t(
        'manageshipmentsscreen.shipmentdetails.orderItems.GRNcolumns.grn'
      )}`;
    },
    accessor: 'grnRefNo',
    align: 'center',
    setWidth: '6.944vw !important',
    Cell: (data) => {
      const {
        row: { original },
      } = data;
      return original?.grnRefNo ? (
        <Tooltip content={original?.grnRefNo} zIndex={10000000}>
          <span className="text-limit w-100-px">{original?.grnRefNo}</span>
        </Tooltip>
      ) : (
        <span>--</span>
      );
    },
  },
  {
    Header: () => {
      const [t] = useTranslation('manageshipments');
      return `${t(
        'manageshipmentsscreen.shipmentdetails.orderItems.GRNcolumns.invoice'
      )}`;
    },
    accessor: 'invoiceRefNo',
    align: 'center',
    Cell: (data) => {
      const {
        row: { original },
      } = data;

      return original?.invoiceRefNo ? (
        <Tooltip content={original?.invoiceRefNo} zIndex={10000000}>
          <span className="text-limit w-100-px">{original?.invoiceRefNo}</span>
        </Tooltip>
      ) : (
        <span>--</span>
      );
    },
  },
];

export const shipmentDetailsOrderItemsColumn = (bookingType) => {
  const isConsolidated = bookingType?.toLowerCase() === 'consolidated';
  return [
    {
      Header: () => {
        const [t] = useTranslation('manageshipments');
        return t('manageshipmentsscreen.shipmentdetails.orderItems.order');
      },
      accessor: 'poNumber',
      align: 'center',
      Cell: (data) => {
        const {
          row: { original },
        } = data;
        return original?.poNumber ? original?.poNumber : <span>--</span>;
      },
    },
    {
      Header: () => {
        const [t] = useTranslation('manageshipments');
        return `${t(
          'manageshipmentsscreen.shipmentdetails.orderItems.orderItems'
        )}`;
      },
      accessor: 'itemSequenceId',
      align: 'center',
      Cell: (data) => {
        const {
          row: { original },
        } = data;
        return original?.itemSequenceId ? (
          original?.itemSequenceId
        ) : (
          <span>--</span>
        );
      },
    },
    {
      Header: () => {
        const [t] = useTranslation('manageshipments');
        return `${t(
          'manageshipmentsscreen.shipmentdetails.orderItems.materialId'
        )}`;
      },
      accessor: 'itemCode',
      align: 'center',
      Cell: (data) => {
        const {
          row: { original },
        } = data;
        return original?.itemCode ? original?.itemCode : <span>--</span>;
      },
    },
    {
      Header: () => {
        const [t] = useTranslation('manageshipments');
        return `${t(
          'manageshipmentsscreen.shipmentdetails.orderItems.materialgroup'
        )}`;
      },
      accessor: 'materialGroup',
      align: 'center',
      Cell: (data) => {
        const {
          row: { original },
        } = data;
        return original?.materialGroup ? (
          original?.materialGroup
        ) : (
          <span>--</span>
        );
      },
    },
    {
      Header: () => {
        const [t] = useTranslation('manageshipments');
        return `${t(
          'manageshipmentsscreen.shipmentdetails.orderItems.shipmentQuantity'
        )}`;
      },
      accessor: 'qtyShipped',
      align: 'center',
      Cell: (data) => {
        const {
          row: { original },
        } = data;
        return original?.qtyShipped ? original?.qtyShipped : <span>--</span>;
      },
    },
    ...(isConsolidated
      ? [
          {
            Header: () => {
              const [t] = useTranslation('manageshipments');
              return `${t(
                'manageshipmentsscreen.shipmentdetails.orderItems.grnQuantity'
              )}`;
            },
            accessor: 'grnQuantity',
            align: 'right',
            Cell: (data) => {
              const {
                row: { original },
              } = data;
              return original?.grnDetails ? (
                <Tooltip content={original?.grnQuantity} zIndex={1000}>
                  <CountCard
                    data={original?.grnDetails}
                    label={formatNumberUpTo3Decimals(original?.grnQuantity)}
                    enableDynamicTable={true}
                    columns={shipmentOrderGrnColumns}
                    disableBorder
                  />
                </Tooltip>
              ) : (
                <span>--</span>
              );
            },
          },
          {
            Header: () => {
              const [t] = useTranslation('manageshipments');
              return (
                <span className="no-wrap">
                  {t(
                    'manageshipmentsscreen.shipmentdetails.orderItems.inboundDelivery'
                  )}{' '}
                  #
                </span>
              );
            },
            accessor: 'inboundNo',
            align: 'center',
            Cell: (data) => {
              const {
                row: { original },
              } = data;

              return Array.isArray(original?.inboundNo) &&
                original?.inboundNo?.length > 0 ? (
                <div className="d-flex align-center">
                  <Button
                    label={original?.inboundNo[0]}
                    className="link-button link"
                  />
                  {original?.inboundNo?.length > 1 && (
                    <Action
                      label={`+${original.inboundNo.length - 1}`}
                      className="icon-button ml-5 primary s-small justify-center"
                      containerClass="item-hover"
                    >
                      {original?.inboundNo?.slice(1).map((inbound) => (
                        <button type="button">{inbound}</button>
                      ))}
                    </Action>
                  )}
                </div>
              ) : (
                <span>--</span>
              );
            },
          },
          {
            Header: () => {
              const [t] = useTranslation('manageshipments');
              return `${t(
                'manageshipmentsscreen.shipmentdetails.orderItems.deliveryDate'
              )}`;
            },
            accessor: 'completedDate',
            align: 'center',
            Cell: (data) => {
              const {
                row: { original },
              } = data;
              return original?.completedDate ? (
                getUserTimeZoneDateFormat(original?.completedDate)
              ) : (
                <span>--</span>
              );
            },
          },
        ]
      : []),
  ];
};

/* eslint-disable react/prop-types */

export const costDetailsColumns = () => [
  {
    Header: () => {
      const [t] = useTranslation('manageshipments');
      return t(
        'manageshipmentsscreen.shipmentdetails.costdetails.columns.invoiceName'
      );
    },
    accessor: 'invoiceName',
  },
  {
    Header: () => {
      const [t] = useTranslation('manageshipments');
      return t(
        'manageshipmentsscreen.shipmentdetails.costdetails.columns.invoiceNumber'
      );
    },
    accessor: 'invoiceNumber',
  },
  {
    Header: () => {
      const [t] = useTranslation('manageshipments');
      return t(
        'manageshipmentsscreen.shipmentdetails.costdetails.columns.invoiceAmount'
      );
    },
    accessor: 'amount',
    align: 'left',
    Cell: (data) => {
      const {
        row: { original },
      } = data;

      const { amount, currency } = original;
      return amount != null ? `${amount.toFixed(2)} ${currency || ''}` : '--';
    },
  },
];
